import { inject, Injectable } from '@angular/core';
import { distinctUntilChanged, map, merge, Observable, shareReplay, startWith, switchMap } from 'rxjs';
import { injectAiChatEventNotifier } from './ai-chat-events.provider';
import { AiChatApiService, InitThreadResponse } from '@panaya/ai-chat/domain';
import { busySignal, combineBusySignals } from '@panaya/ai-chat/utils';
import { AiChatThreadIdProviderService } from './ai-chat-thread-id-provider.service';

export interface LatestThreadId {
    value: string | null;
    source: 'init' | 'close' | 'restore' | 'prompt';
}

export type LatestThreadData = {
    thread: LatestThreadId;
};

/**
 * Represents the thread with the AI assistant.
 * This connects thread related UI events with the AI API to manage the active thread.
 */
@Injectable({ providedIn: 'root' })
export class AiChatThreadService {
    private readonly uiEvents = injectAiChatEventNotifier();
    private readonly ai = inject(AiChatApiService);
    private readonly threadIdProvider = inject(AiChatThreadIdProviderService);
    private readonly threadData = this.createThreadDataFeed();

    /**
     * Emits the id of the active thread.
     * For a freshly initialized thread or a restored thread, this will emit the thread id.
     * If the thread is closed, this will emit `null`.
     */
    public readonly id = this.createThreadIdFeed();
    /**
     * Emits `true` when a thread is active, meaning {@link id} has a valid thread id.
     * Emits `false` when there is no active thread, meaning the thread is closed and {@link id} has `null`.
     */
    public readonly isActive = this.createIsActiveFeed();
    /**
     * Emits `true` when the thread is in the process of being initialized or restored.
     * Emits `false` when initialization or restoration of the thread has finished.
     */
    public readonly busy = combineBusySignals(
        busySignal(this.uiEvents.initThreadRequest, this.id),
        busySignal(this.uiEvents.restoreThreadRequest, this.id)
    );

    private createThreadDataFeed(): Observable<LatestThreadData> {
        const threadInitialized = this.uiEvents.initThreadRequest.pipe(
            switchMap(() => this.ai.initThread()),
            map(({ thread_id }: InitThreadResponse) => ({ thread: { value: thread_id, source: 'init' as const } }))
        );
        const threadClosed = this.uiEvents.closeThreadRequest.pipe(map(() => ({ thread: { value: null, source: 'close' as const } })));
        const threadRestored = this.uiEvents.restoreThreadRequest.pipe(
            map((threadId) => ({ thread: { value: threadId, source: 'restore' as const } }))
        );
        const threadFromPrompt = this.threadIdProvider.threadIdFromPrompt$.pipe(
            map((threadId) => {
                return { thread: { value: threadId, source: 'prompt' as const } };
            })
        );

        return merge(threadInitialized, threadClosed, threadRestored, threadFromPrompt).pipe(
            startWith({ thread: { value: null, source: 'close' as const } }),
            shareReplay(1)
        );
    }

    private createThreadIdFeed(): Observable<LatestThreadId> {
        return this.threadData.pipe(map((threadData) => threadData.thread));
    }

    private createIsActiveFeed(): Observable<boolean> {
        return this.id.pipe(
            map(({ value, source }) => {
                // Chat is active if:
                // 1. We have a valid thread_id, OR
                // 2. Thread was initialized (even if thread_id is null), OR
                // 3. Thread was restored with a thread_id
                return !!value || source === 'init';
            }),
            startWith(false),
            distinctUntilChanged(),
            shareReplay(1)
        );
    }
}
